export const GAME_WIDTH = 1600;
export const GAME_HEIGHT = 900;

export const TUNING = {
  joint: {
    initialLengthPct: 100,
    depletionPctPerSec: 3.0,
    cardboardZonePct: 12,
    paperVisualLengthPx: 360,
    filterVisualLengthPx: 60
  },
  lung: {
    maxCapacityPct: 100,
    inhaleDepletionPctPerSec: 20,
    idleRecoveryPctPerSec: 5,
    comboWindowRecoveryPctPerSec: 25,
    /**
     * As the joint shortens, the lungs deplete faster. The multiplier follows a
     * cubic curve: ~1.0× through the first half of the joint, then accelerates
     * sharply, reaching `maxMultiplier` at empty joint.
     * Formula: 1 + (maxMultiplier - 1) * (1 - jointPct/100)^3.
     */
    jointShortMaxMultiplier: 4.0
  },
  choke: {
    risePerSec: 28,
    threshold: {
      none: 50,
      mildWarning: 70,
      heavyWarning: 90,
      forced: 100
    },
    probabilityPerSec: {
      mild: { from: 50, to: 70, prob: [0, 5] },
      moderate: { from: 70, to: 90, prob: [5, 25] },
      severe: { from: 90, to: 99, prob: [30, 95] }
    }
  },
  combo: {
    windowMs: 2500
  },
  turn: {
    coinFlipDurationMs: 2200,
    passAnimationMs: 900,
    chokeAnimationMs: 1400
  },
  hud: {
    lungBarWidth: 300,
    chokeBarWidth: 224
  },
  qte: {
    /** Min lung capacity at inhale-start to make this inhale QTE-eligible. */
    eligibilityLungPct: 75,
    /** When lung drops below this during an eligible inhale, the QTE activates:
     *  the line starts sweeping right→left. Paired with `sweepEndLungPct`, the
     *  sweep is mapped to the lung range [trigger..end], so the line reaches
     *  the bar's center at the lung midpoint — i.e. (trigger+end)/2 = 15%
     *  remaining with the defaults below. Hitting center = released at 15%. */
    triggerLungPct: 30,
    /** Lung level at which the line finishes its left-edge sweep. The bar's
     *  x position is a linear remap of live lung% across [sweepEnd..trigger] →
     *  [0..1], so sweep speed naturally scales with the drain rate (which
     *  depends on joint length). Shorter joint → faster drain → faster line. */
    sweepEndLungPct: 0,
    /** Time for the moving line to travel from right edge to left edge.
     *  Kept only as a safety ceiling / for AI scheduling. Actual sweep speed
     *  is driven by lung drain (see above). */
    durationMs: 1000,
    /** Golden target zone width as a % of the bar width (centered at 50%). */
    goldenZoneWidthPct: 12,
    /** Random reward range: choke reduction on a successful release. */
    minRewardPct: 5,
    maxRewardPct: 25,
    /** Bar visual dims. */
    barWidthDesktop: 400,
    barHeightDesktop: 28,
    barWidthMobile: 280,
    barHeightMobile: 22,
    /** Vertical center of the QTE bar on screen. */
    yDesktop: 380,
    yMobile: 300
  },
  score: {
    secondsPerTickMs: 100
  }
} as const;

export type Side = 'player' | 'ai';
